import React, { useState, useEffect, useCallback } from "react";
import { TbCurrentLocation } from "react-icons/tb";
import useGetLanguage from "../../../../../utils/useGetLanguage";
import { LanguageText } from "../../../../../dictionary/ChartsText";
export interface Coordinates {
  lat: number;
  lng: number;
}

interface MarkerCoordinatesProps {
  initialLocation: Coordinates | null;
  onLocationChange?: (newLocation: Coordinates) => void;
}

const MarkerCoordinates: React.FC<MarkerCoordinatesProps> = ({
  initialLocation,
  onLocationChange,
}) => {
  const language: LanguageText = useGetLanguage({
    fileName: "ChartsText",
  });

  const [location, setLocation] = useState<Coordinates>({
    lat: initialLocation?.lat ?? 0,
    lng: initialLocation?.lng ?? 0,
  });

  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (initialLocation) {
      setLocation(initialLocation);
    }
  }, [initialLocation]);

  const handleLocationChange = useCallback(
    ({ target: { name, value } }: React.ChangeEvent<HTMLInputElement>) => {
      setLocation((prev) => {
        const newLocation = {
          ...prev,
          [name]: parseFloat(value),
        };

        if (onLocationChange) {
          onLocationChange(newLocation);
        }
        return newLocation;
      });
    },
    [onLocationChange]
  );

  const getCurrentLocation = useCallback(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          const newLocation = {
            lat: latitude,
            lng: longitude,
          };
          setLocation(newLocation);
          if (onLocationChange) {
            onLocationChange(newLocation);
          }
          setError(null);
        },
        (err) => {
          setError(
            language?.charts?.map?.markers?.notification?.errorMessages?.enableLocation
          );
        }
      );
    } else {
      setError(
        language?.charts?.map?.markers?.notification?.errorMessages
          ?.geolocationNotSupported
      );
    }
  }, [onLocationChange]);

  return (
    <div className="w-full p-4 mb-4 bg-white shadow-lg rounded-lg flex flex-col space-y-2">
      <h4 className="text-sm font-semibold text-center m-0">
        {language?.charts?.map?.markers?.notification?.titles?.coordinate}
      </h4>
      <div className="flex flex-wrap justify-between items-center space-x-2">
        <div className="flex-1">
          <input
            type="number"
            name="lat"
            value={location.lat || ""}
            onChange={handleLocationChange}
            className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
            placeholder="Latitude"
            step="any"
            aria-label="Latitude"
          />
        </div>

        <div className="flex-1">
          <input
            type="number"
            name="lng"
            value={location.lng || ""}
            onChange={handleLocationChange}
            className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
            placeholder="Longitude"
            step="any"
            aria-label="Longitude"
          />
        </div>

        <button
          onClick={getCurrentLocation}
          className="p-2 bg-gray-200 rounded-full hover:bg-gray-400 focus:outline-none"
          title="Current Location"
          aria-label="Get Current Location"
        >
          <TbCurrentLocation className="w-5 h-5" />
        </button>
      </div>

      {error && <p className="text-red-500 text-xs text-center">{error}</p>}
    </div>
  );
};

export default MarkerCoordinates;
